// src/pages/AdminXReplies/AdminXReplies.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  ButtonGroup,
  Modal,
  Form,
} from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminXReplies.css";

const API_BASE = process.env.REACT_APP_API_BASE;

async function authHeader() {
  const session = await Auth.currentSession();
  const token = session.getAccessToken().getJwtToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function statusBadge(status) {
  const s = (status || "PENDING").toUpperCase();
  if (s === "PENDING") return <Badge bg="warning">Pending</Badge>;
  if (s === "APPROVED") return <Badge bg="primary">Approved</Badge>;
  if (s === "REJECTED") return <Badge bg="secondary">Rejected</Badge>;
  if (s === "POSTED") return <Badge bg="success">Posted</Badge>;
  if (s === "ERROR") return <Badge bg="danger">Error</Badge>;
  return <Badge bg="dark">{s}</Badge>;
}

function fmtDate(msOrIso) {
  if (!msOrIso) return "—";
  const d = typeof msOrIso === "number" ? new Date(msOrIso) : new Date(msOrIso);
  if (Number.isNaN(d.getTime())) return String(msOrIso);
  return d.toLocaleString();
}

export default function AdminXReplies() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending"); // pending|approved|rejected|posted|error|all
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Edit modal
  const [editModal, setEditModal] = useState({
    show: false,
    item: null,
    replyText: "",
  });

  const [saving, setSaving] = useState(false);
  const [postingId, setPostingId] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const idPayload = user?.signInUserSession?.idToken?.payload || {};
        const groups = idPayload["cognito:groups"] || idPayload["custom:groups"] || [];

        if (Array.isArray(groups) && (groups.includes("Support") || groups.includes("Admin"))) {
          setAuthorized(true);
          await fetchReplies("pending");
        } else {
          setAuthorized(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth error:", err);
        setAuthorized(false);
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterLabel = useMemo(() => {
    return (
      {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        posted: "Posted",
        error: "Error",
        all: "All",
      }[filter] || "Pending"
    );
  }, [filter]);

  const fetchReplies = async (status = filter) => {
    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const headers = await authHeader();
      let url = `${API_BASE}/admin/x/replies`;

      if (status && status !== "all") {
        url += `?status=${encodeURIComponent(status)}`;
      } else {
        url += `?status=all`;
      }

      const res = await fetch(url, { method: "GET", headers });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load drafts: ${res.status}`);
      }

      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error("Fetch replies error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onFilterChange = async (newFilter) => {
    setFilter(newFilter);
    await fetchReplies(newFilter);
  };

  const updateDraft = async (tweetId, patch) => {
    setError("");
    setSuccessMsg("");
    const headers = await authHeader();

    const res = await fetch(`${API_BASE}/admin/x/replies/${encodeURIComponent(tweetId)}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(patch),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to update: ${res.status}`);
    }

    const data = await res.json();
    const updated = data.item;

    setItems((prev) => prev.map((i) => ((i.pk || i.tweetId) === tweetId ? updated : i)));
    return updated;
  };

  const postReply = async (tweetId) => {
    setError("");
    setSuccessMsg("");
    const headers = await authHeader();

    const res = await fetch(`${API_BASE}/admin/x/replies/${encodeURIComponent(tweetId)}`, {
      method: "POST",
      headers,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to post: ${res.status}`);
    }

    return await res.json();
  };

  const openEditModal = (item) => {
    setError("");
    setSuccessMsg("");
    setEditModal({
      show: true,
      item,
      replyText: item.replyText || "",
    });
  };

  const closeEditModal = () => {
    setEditModal({ show: false, item: null, replyText: "" });
  };

  const saveEdits = async () => {
    if (!editModal.item) return;
    const tweetId = editModal.item.pk || editModal.item.tweetId;
    const text = (editModal.replyText || "").trim();

    if (!text) {
      setError("Reply cannot be empty.");
      return;
    }

    setSaving(true);
    try {
      await updateDraft(tweetId, { replyText: text });
      setSuccessMsg("Draft updated.");
      closeEditModal();
    } catch (err) {
      console.error("Save edits error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const approve = async (item) => {
    const tweetId = item.pk || item.tweetId;
    try {
      await updateDraft(tweetId, { status: "APPROVED" });
      setSuccessMsg("Approved.");
      await fetchReplies(filter);
    } catch (err) {
      console.error("Approve error:", err);
      setError(err.message);
    }
  };

  const reject = async (item) => {
    const tweetId = item.pk || item.tweetId;
    try {
      await updateDraft(tweetId, { status: "REJECTED" });
      setSuccessMsg("Rejected.");
      await fetchReplies(filter);
    } catch (err) {
      console.error("Reject error:", err);
      setError(err.message);
    }
  };

  const postNow = async (item) => {
    const tweetId = item.pk || item.tweetId;

    if ((item.status || "").toUpperCase() !== "APPROVED") {
      const ok = window.confirm("This draft is not APPROVED yet. Post anyway?");
      if (!ok) return;
    }

    setPostingId(tweetId);
    try {
      const result = await postReply(tweetId);

      setSuccessMsg(
        result.status === "already_posted" ? "Already posted." : "Posted to X ✅"
      );

      await fetchReplies(filter);
    } catch (err) {
      console.error("PostNow error:", err);
      setError(err.message);
    } finally {
      setPostingId(null);
    }
  };

  if (loading && !items.length) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4 text-center">
            <Spinner animation="border" /> Loading X reply drafts…
          </div>
        </Container>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4">
            <Alert variant="danger">You’re not authorized to view this page.</Alert>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">𝕏</div>
            <div>
              <h3 className="admin-page-title">X Reply Drafts – {filterLabel}</h3>
              <div className="admin-page-subtitle">
                Review AI-generated reply drafts, approve/edit, then post to X.
              </div>
            </div>
          </div>
        </div>

        <div className="admin-page-body">
          <div className="admin-panel mb-3">
            <div className="admin-filter-row">
              <div />

              <div className="admin-filter-actions">
                <ButtonGroup size="sm">
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "pending" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("pending")}
                  >
                    Pending
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "approved" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("approved")}
                  >
                    Approved
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "posted" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("posted")}
                  >
                    Posted
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "rejected" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("rejected")}
                  >
                    Rejected
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "all" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("all")}
                  >
                    All
                  </Button>
                </ButtonGroup>

                <Button variant="outline-secondary" size="sm" onClick={() => fetchReplies(filter)}>
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <div className="admin-panel">
            {items.length === 0 ? (
              <p className="mb-0">No drafts for this filter 🎉</p>
            ) : (
              <Table className="admin-table" striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Author</th>
                    <th>Original</th>
                    <th>Draft Reply</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => {
                    const tweetId = i.pk || i.tweetId;
                    const isPosting = postingId === tweetId;

                    return (
                      <tr key={tweetId}>
                        <td style={{ whiteSpace: "nowrap" }}>{statusBadge(i.status)}</td>

                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(i.createdAt)}</td>

                        <td style={{ whiteSpace: "nowrap" }}>@{i.author || "—"}</td>

                        <td style={{ minWidth: 260, maxWidth: 360, whiteSpace: "pre-wrap" }}>
                          {i.originalText || "—"}
                        </td>

                        <td style={{ minWidth: 260, maxWidth: 420, whiteSpace: "pre-wrap" }}>
                          {i.replyText || "—"}
                          <div className="admin-char-count">
                            {((i.replyText || "").length || 0)}/240
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1 admin-row-actions">
                            <Button size="sm" variant="outline-secondary" onClick={() => openEditModal(i)}>
                              Edit
                            </Button>

                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => approve(i)}
                              disabled={(i.status || "").toUpperCase() === "APPROVED"}
                            >
                              Approve
                            </Button>

                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => reject(i)}
                              disabled={(i.status || "").toUpperCase() === "REJECTED"}
                            >
                              Reject
                            </Button>

                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => postNow(i)}
                              disabled={!!postingId}
                            >
                              {isPosting ? "Posting…" : "Post to X"}
                            </Button>

                            {i.postedTweetId && (
                              <small className="text-muted" style={{ fontSize: "0.8rem" }}>
                                Posted ID: {i.postedTweetId}
                              </small>
                            )}

                            {i.error && (
                              <small
                                className="text-danger"
                                style={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}
                              >
                                {i.error}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </div>

        {/* Edit Modal */}
        <Modal className="admin-modal" show={editModal.show} onHide={closeEditModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Draft Reply</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              Keep replies concise and helpful. Avoid medical diagnosis and sensitive info.
            </p>

            <Form.Group controlId="adminXRepliesEditText">
              <Form.Label>Reply Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={editModal.replyText}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    replyText: e.target.value,
                  }))
                }
              />
              <div className="text-muted mt-1" style={{ fontSize: "0.85rem" }}>
                {editModal.replyText.length}/240
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={saveEdits} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
